/**
 * Class Content Page
 * Shows study materials and content for learners when they click on a class
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Paper, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from '../../utils/axiosConfig';
import StudyGuide from './StudyGuide';
import Gr10ContentHub from './Gr10ContentHub';

interface SchoolClass {
  id: number;
  name: string;
  subject: string;
  description: string;
  grade_level: number | null;
}

// Classes that should show the study guide
const STUDY_GUIDE_CLASSES = [
  'Graad 11 BOS 11-2/43',
  'Graad 11 BOS 110',
  'Graad 11 SB 11-6/42'
];

const ClassContentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [classData, setClassData] = useState<SchoolClass | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClassData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/school-classes/${id}/`);
        setClassData(response.data);
      } catch (err: any) {
        console.error('Error fetching class data:', err);
        setError(err.response?.data?.error || 'Failed to load class information');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchClassData();
    }
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !classData) {
    return (
      <Box p={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="error">
            {error || 'Class not found'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (classData.grade_level === 10) {
    return <Gr10ContentHub />;
  }

  // Check if this class should show the Grade 11 study guide
  const showStudyGuide = STUDY_GUIDE_CLASSES.includes(classData.name);

  if (showStudyGuide) {
    return <StudyGuide />;
  }

  // For other classes, show a coming soon message
  return (
    <Box p={3}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/dashboard')}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          {classData.name}
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          {classData.subject}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Study materials for this class will be available soon.
        </Typography>
      </Paper>
    </Box>
  );
};

export default ClassContentPage;
